import Image from '@/components/Image';
import {
  ChatBubbleIcon,
  EyebrowText,
  Heading,
  LeftColumn,
  PaperPlaneIcon,
  RightColumn,
  Screenshot,
  ScreenshotWrapper,
  ShootingStarIcon,
  SmileyIcon,
  StyledGroupedInput,
  TextContainer,
  Wrapper,
} from '@/components/heros/UIHero/components';
import { HubspotFieldNames } from '@/constants/hubspot';
import { GlobalContext } from '@/context';
import { ModuleUihero_BlockType } from '@/graphql';
import gtm from '@/lib/gtm';
import useBreakpoint from '@/lib/hooks/useBreakpoint';
import { parseHtml } from '@/lib/parse-html';
import { getSingle, maybeNotSet } from '@/lib/typescript-helpers';
import { FC, ReactNode, useContext } from 'react';
import { UserContext } from '@/context/user';

interface UIHeroProps {
  heading: string;
  content: ReactNode;
  image: ReactNode;
  ctaText?: string;
  eyebrowText?: string;
}

const UIHero: FC<UIHeroProps> = (props) => {
  const { toggleGetStartedModal } = useContext(GlobalContext);
  const { userData, setUserData } = useContext(UserContext);
  const [currentBreakpoint] = useBreakpoint();

  return (
    <header>
      <Wrapper>
        <LeftColumn>
          {props.eyebrowText && <EyebrowText id="hero-eyebrow">{props.eyebrowText}</EyebrowText>}

          <Heading id="hero-heading">{props.heading}</Heading>

          <TextContainer id="hero-text">{props.content}</TextContainer>

          <StyledGroupedInput
            name={HubspotFieldNames.Email}
            type="email"
            value={userData[HubspotFieldNames.Email]}
            placeholder="Your email address"
            submitText={props.ctaText!}
            split={currentBreakpoint === 'mobile'}
            onValueChange={(email) => setUserData({ [HubspotFieldNames.Email]: email })}
            onSubmit={() => {
              toggleGetStartedModal(true);

              gtm.track('submit_form', {
                location: 'Hero',
                form_name: 'Email Capture',
              });
            }}
            onButtonClick={() => {
              gtm.track('click', {
                element_type: 'button',
                location: 'Hero',
                element_label: maybeNotSet(props.ctaText),
              });
            }}
          />
        </LeftColumn>

        <RightColumn>
          <ScreenshotWrapper>
            <PaperPlaneIcon />
            <ChatBubbleIcon />
            <Screenshot>{props.image}</Screenshot>
            <ShootingStarIcon />
            <SmileyIcon />
          </ScreenshotWrapper>
        </RightColumn>
      </Wrapper>
    </header>
  );
};

UIHero.defaultProps = {
  ctaText: 'Get started',
};

export default UIHero;

export const UIHeroPropsTransformer = (module: ModuleUihero_BlockType): UIHeroProps => {
  let image: ReactNode = null;

  const asset = getSingle(module.uiImage);

  if (asset && asset.url) {
    image = <Image src={asset.url} width={asset.width ?? 0} height={asset.height ?? 0} alt="" />;
  }

  return {
    heading: module.heading ?? '',
    content: parseHtml(module.text),
    image,
    ctaText: module.ctaText ?? undefined,
  };
};
